import { concat, merge, Observable, of } from 'rxjs';
import { pluck, skipWhile, throttleTime, scan, concatMap, map } from 'rxjs/operators';
import { ProcessLine, Area, OptionalGroupedParsedChartData, WarningIndexSingleParamData, ProcessType } from '../../types';
import { withLogger } from '../LogerServices';
import { getINTBMLCWarningIndexTableDataWithFactoryScheme, getMLCSingleParamTrendsData, getTBCFMLCWarningIndexTableDataWithFactoryScheme } from '../MLCServices/MLCApi';
import { mapToChartData, pushNewDataToCollectedData } from './chartConfig';
import { getMLCINTBLast30MinutesChartsData, getMLCTBCFLast30MinutesChartsData } from './initialDataChartService';

const getMLCINTBSolutionData = (name: string, line: ProcessLine): Observable<Partial<WarningIndexSingleParamData>> =>
    getINTBMLCWarningIndexTableDataWithFactoryScheme().pipe(
        pluck(line, name),
        skipWhile(data => !data) // Skip when undefined 
    );

const getMLCTBCFSolutionData = (name: string): Observable<Partial<WarningIndexSingleParamData>> =>
    getTBCFMLCWarningIndexTableDataWithFactoryScheme().pipe(
        pluck(name),
        skipWhile(data => !data) // Skip when undefined 
    );

const mapToChartDataWrapper = (observable: Observable<unknown>, params: string[]) => {
    return observable.pipe(
        map(data => mapToChartData(data, params)),
        skipWhile(data => Object.values(data).length === 0),
    )
};

const chartDataObservable = (name: string, params: string[], line: ProcessLine, isINTB: boolean): Observable<OptionalGroupedParsedChartData> =>
    merge(
        mapToChartDataWrapper(getMLCSingleParamTrendsData(name, line), params),
        mapToChartDataWrapper(isINTB ? getMLCINTBSolutionData(name, line) : getMLCTBCFSolutionData(name), params),
    );

const chartDataWithInitialDataObservable = (param: string, area: Area, line?: ProcessLine) => {
    const isINTB = area === Area.INTB;
    const getChartInitialData = isINTB
        ? getMLCINTBLast30MinutesChartsData(param, line)
        : getMLCTBCFLast30MinutesChartsData(param);
    const logText = isINTB ? Area.INTB : Area.TBCF
    return getChartInitialData.pipe(
        concatMap(data => {
            const params = Object.keys(data);
            return concat(
                withLogger(of(data), ProcessType.CHART, {isInitial: true, optionalText: logText}),
                withLogger((chartDataObservable(param, params, line, isINTB)), ProcessType.CHART, {optionalText: logText})
            ).pipe(
                scan(pushNewDataToCollectedData),
                throttleTime(3000), // 3 sec
            )
        }),
    ) as Observable<OptionalGroupedParsedChartData>;
};


export const getINTBChartDataWithInitialDataObservable = (param: string, line?: ProcessLine) =>
    chartDataWithInitialDataObservable(param, Area.INTB, line);

export const getTBCFChartDataWithInitialDataObservable = (param: string) =>
    chartDataWithInitialDataObservable(param, Area.TBCF);
